import { inject, Injectable } from "@angular/core";
import { mutationOptions, QueryClient, queryOptions } from "@tanstack/angular-query-experimental";

import { S3FileService } from "@intocare/aws";
import {
	CollectionBundle,
	DataBundle,
	DataBundleWithActions,
	FileDownloadBundle,
	PagedCollectionBundle,
	PagingParams,
} from "@intocare/datatypes/lib/bundle";

import {
	AssessmentDetailBundle,
	AssessmentPlannedDateSet,
	AssessmentPlannedDateUnset,
	AssessmentPlannedDateUpdate,
	AssessmentSummary,
	SubmitAssessmentResponse,
	SubmittedAssessmentDetail,
} from "./assessment";
import { AssessmentCreation } from "./assessment-creation";
import { AssessmentUploadSubmission, DeleteAssessmentUpload } from "./assessment-upload";
import { AttachedDocument } from "./attached-document";
import {
	CareDataContext,
	FollowUpPlanFilters,
	ObservationFilters,
	ObservationOfTypeFallReportFilters,
} from "./care-data";
import { CareDataApi } from "./care-data.api";
import { EvaluationDetail, EvaluationSummary } from "./evaluation";
import { FollowUpPlanDetails, FollowUpPlanForSiteSummary, FollowUpPlanStatus } from "./follow-up-plan";
import { FollowUpPlanCreation } from "./follow-up-plan-creation";
import { convertToFollowUpPlanCreationDto } from "./follow-up-plan-creation.dto";
import { FollowUpPlanEdit } from "./follow-up-plan-edit";
import { convertToFollowUpPlanEditDto } from "./follow-up-plan-edit.dto";
import { FinalizedFollowUpPlanEvaluationEdit, FollowUpPlanEvaluationSave } from "./follow-up-plan-evaluation";
import { FollowUpPlanObservationLink } from "./follow-up-plan-observation-links";
import { FollowUpPlanUploadSubmission } from "./follow-up-plan-upload";
import { convertToFollowUpPlanDetails, convertToFollowUpPlanForSiteSummary } from "./follow-up-plan.dto";
import {
	ObservationOfTypeFallCreation,
	ObservationOfTypeIncontinenceCreation,
	ObservationOfTypeInfectionCreation,
	ObservationOfTypeNutritionAndHydrationCreation,
	ObservationOfTypeOtherCreation,
	ObservationOfTypePhysicalComplaintCreation,
} from "./observation-creation";
import {
	ObservationOfTypeFallEdit,
	ObservationOfTypeIncontinenceEdit,
	ObservationOfTypeInfectionEdit,
	ObservationOfTypeNutritionAndHydrationEdit,
	ObservationOfTypeOtherEdit,
	ObservationOfTypePhysicalComplaintEdit,
} from "./observation-edit";
import { ObservationHistoryEvent } from "./observation-history-event";
import {
	ObservationLink,
	ObservationLinkType,
	ObservationOfTypeFallWithLinks,
	ObservationOfTypeIncontinenceWithLinks,
	ObservationOfTypeInfectionWithLinks,
	ObservationOfTypeNutritionAndHydrationWithLinks,
	ObservationOfTypeOtherWithLinks,
	ObservationOfTypePhysicalComplaintWithLinks,
} from "./observation-links";
import { observationLinkToDto } from "./observation-links.dto";
import { ObservationOfTypeFall, ObservationSummary, ObservationSummaryWithUser } from "./observations";
import { RestrictiveMeasuresForResident } from "./restrictive-measures";

const careDataKeys = {
	base(context: CareDataContext) {
		return ["care", "sites", context.siteId] as const;
	},
	baseObservations(context: CareDataContext) {
		return [...this.base(context), "observations"] as const;
	},
	baseObservationLists(context: CareDataContext) {
		return [...this.baseObservations(context), "lists"] as const;
	},
	observationsOfResident(context: CareDataContext, params: { residentId: string; filters: ObservationFilters }) {
		return [...this.baseObservationLists(context), "resident", params.residentId, params.filters] as const;
	},
	observationsOfTypeFall(
		context: CareDataContext,
		params: { paging: PagingParams; filters: ObservationOfTypeFallReportFilters },
	) {
		return [...this.baseObservationLists(context), "FALL", { ...params.paging, ...params.filters }] as const;
	},
	observationsOfLinkable(
		context: CareDataContext,
		params: {
			source: { residentId: string; observationId: string };
			linkType: ObservationLinkType;
			filters: ObservationFilters;
		},
	) {
		return [...this.baseObservationLists(context), "linkable", params.source, params.linkType, params.filters] as const;
	},
	observationsOfLinkableForFollowUpPlan(
		context: CareDataContext,
		params: {
			source: { residentId: string; followUpPlanId: string };
			filters: ObservationFilters;
		},
	) {
		return [
			...this.baseObservationLists(context),
			"linkable-for-follow-up-plan",
			params.source,
			params.filters,
		] as const;
	},
	baseObservationDetails(context: CareDataContext) {
		return [...this.baseObservations(context), "details"] as const;
	},
	observationSummary(context: CareDataContext, params: { observationId: string }) {
		return [...this.baseObservationDetails(context), "summary", params.observationId] as const;
	},
	observationDetails(context: CareDataContext, params: { observationId: string }) {
		return [...this.baseObservationDetails(context), "details", params.observationId] as const;
	},
	observationHistory(context: CareDataContext, params: { observationId: string }) {
		return [...this.baseObservationDetails(context), "history", params.observationId] as const;
	},
	baseFollowUpPlans(context: CareDataContext) {
		return [...this.base(context), "follow-up-plans"] as const;
	},
	baseFollowUpPlanLists(context: CareDataContext) {
		return [...this.baseFollowUpPlans(context), "lists"] as const;
	},
	followUpPlansForSite(
		context: CareDataContext,
		params: { residentId?: string; filters: FollowUpPlanFilters; paging: PagingParams },
	) {
		return [
			...this.baseFollowUpPlanLists(context),
			"site",
			{ ...params.paging, ...params.filters, residentId: params.residentId },
		] as const;
	},
	baseFollowUpPlanDetails(context: CareDataContext) {
		return [...this.baseFollowUpPlans(context), "details"] as const;
	},
	followUpPlanDetails(context: CareDataContext, params: { followUpPlanId: string }) {
		return [...this.baseFollowUpPlanDetails(context), "details", params.followUpPlanId] as const;
	},
	followUpPlanEvaluationDetails(context: CareDataContext, params: { followUpPlanId: string; evaluationId: string }) {
		return [
			...this.baseFollowUpPlanDetails(context),
			"evaluation",
			params.followUpPlanId,
			params.evaluationId,
			"details",
		] as const;
	},
	followUpPlanEvaluations(context: CareDataContext, params: { followUpPlanId: string }) {
		return [...this.baseFollowUpPlanDetails(context), "evaluations", params.followUpPlanId] as const;
	},
	followUpPlanLinkedObservations(context: CareDataContext, params: { followUpPlanId: string }) {
		return [...this.baseFollowUpPlanDetails(context), "linked-observations", params.followUpPlanId] as const;
	},
	followUpPlanDocuments(context: CareDataContext, params: { followUpPlanId: string }) {
		return [...this.baseFollowUpPlanDetails(context), "documents", params.followUpPlanId] as const;
	},
	basePatientData(context: CareDataContext) {
		return [...this.base(context), "patient-data"];
	},
	baseRestrictiveMeasures(context: CareDataContext) {
		return [...this.basePatientData(context), "restrictive-measures"];
	},
	restrictiveMeasuresDetails(context: CareDataContext, params: { residentId: string }) {
		return [...this.baseRestrictiveMeasures(context), "details", params.residentId];
	},
	baseAssessments(context: CareDataContext) {
		return [...this.base(context), "assessments"];
	},
	baseAssessmentLists(context: CareDataContext) {
		return [...this.baseAssessments(context), "lists"];
	},
	assessmentsOfResident(context: CareDataContext, params: { residentId: string }) {
		return [...this.baseAssessmentLists(context), "resident", params.residentId];
	},
	baseAssessmentDetails(context: CareDataContext) {
		return [...this.baseAssessments(context), "details"];
	},
	assessmentDetail(context: CareDataContext, params: { assessmentType: string; residentId: string }) {
		return [...this.baseAssessmentDetails(context), "detail", params.assessmentType, params.residentId];
	},
	submittedAssessmentDetail(context: CareDataContext, params: { submittedAssessmentId: string }) {
		return [...this.baseAssessmentDetails(context), "submitted", params.submittedAssessmentId];
	},
	submittedAssessmentDocuments(context: CareDataContext, params: { submittedAssessmentId: string }) {
		return [...this.baseAssessmentDetails(context), "documents", params.submittedAssessmentId];
	},
};

@Injectable({
	providedIn: "root",
})
export class CareDataService {
	private readonly api = inject(CareDataApi);
	private readonly queryClient = inject(QueryClient);
	private readonly s3FileService = inject(S3FileService);

	getObservationsOfResidentQueryOptions(
		context: CareDataContext,
		params: { residentId: string; filters: ObservationFilters },
	) {
		return queryOptions<CollectionBundle<DataBundle<ObservationSummary>>>({
			queryKey: careDataKeys.observationsOfResident(context, params),
			queryFn: () => this.api.getObservationSummariesByResidentId(context, params),
			throwOnError: false,
		});
	}

	getObservationSummaryQueryOptions(context: CareDataContext, params: { observationId: string }) {
		return queryOptions<DataBundle<ObservationSummaryWithUser>>({
			queryKey: careDataKeys.observationSummary(context, params),
			queryFn: () => this.api.getObservationSummaryById(context, params),
			throwOnError: false,
		});
	}

	getObservationOfTypeFallHateoasQueryOptions(context: CareDataContext, params: { observationId: string }) {
		return queryOptions<DataBundleWithActions<ObservationOfTypeFallWithLinks, "update">>({
			queryKey: careDataKeys.observationDetails(context, params),
			queryFn: () => this.api.getHateoasFallObservationById(context, params),
			throwOnError: false,
		});
	}

	createObservationOfTypeFallMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeFallCreation): Promise<{ id: string }> =>
				this.api.createFallObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservationLists(context) });
			},
		});
	}

	editObservationOfTypeFallMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeFallEdit): Promise<void> =>
				this.api.editFallObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservations(context) });
			},
		});
	}

	getObservationOfTypeIncontinenceQueryOptions(context: CareDataContext, params: { observationId: string }) {
		return queryOptions<DataBundleWithActions<ObservationOfTypeIncontinenceWithLinks, "update">>({
			queryKey: careDataKeys.observationDetails(context, params),
			queryFn: () => this.api.getIncontinenceObservationById(context, params),
			throwOnError: false,
		});
	}

	createIncontinenceObservationMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeIncontinenceCreation): Promise<{ id: string }> =>
				this.api.createIncontinenceObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservationLists(context) });
			},
		});
	}

	editObservationOfTypeIncontinenceMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeIncontinenceEdit): Promise<void> =>
				this.api.editIncontinenceObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservations(context) });
			},
		});
	}

	getObservationOfTypeOtherHateoasQueryOptions(context: CareDataContext, params: { observationId: string }) {
		return queryOptions<DataBundleWithActions<ObservationOfTypeOtherWithLinks, "update">>({
			queryKey: careDataKeys.observationDetails(context, params),
			queryFn: () => this.api.getHateoasOtherObservationById(context, params),
			throwOnError: false,
		});
	}

	createObservationOfTypeOtherMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeOtherCreation): Promise<{ id: string }> =>
				this.api.createOtherObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservationLists(context) });
			},
		});
	}

	editObservationOfTypeOtherMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeOtherEdit): Promise<void> =>
				this.api.editOtherObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservations(context) });
			},
		});
	}

	getObservationOfTypeInfectionQueryOptions(context: CareDataContext, params: { observationId: string }) {
		return queryOptions<DataBundleWithActions<ObservationOfTypeInfectionWithLinks, "update">>({
			queryKey: careDataKeys.observationDetails(context, params),
			queryFn: () => this.api.getInfectionObservationById(context, params),
			throwOnError: false,
		});
	}

	createInfectionObservationMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeInfectionCreation): Promise<{ id: string }> =>
				this.api.createInfectionObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservationLists(context) });
			},
		});
	}

	editObservationOfTypeInfectionMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeInfectionEdit): Promise<void> =>
				this.api.editInfectionObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservations(context) });
			},
		});
	}

	getLinkableObservationsQueryOptions(
		context: CareDataContext,
		params: {
			source: { residentId: string; observationId: string };
			linkType: ObservationLinkType;
			filters: ObservationFilters;
		},
	) {
		return queryOptions<CollectionBundle<DataBundleWithActions<ObservationSummary, "link">>>({
			queryKey: careDataKeys.observationsOfLinkable(context, params),
			queryFn: () => this.api.getLinkableObservations(context, params),
			throwOnError: false,
		});
	}

	getLinkableObservationsForFollowUpPlanQueryOptions(
		context: CareDataContext,
		params: {
			source: { residentId: string; followUpPlanId: string };
			filters: ObservationFilters;
		},
	) {
		return queryOptions<CollectionBundle<DataBundleWithActions<ObservationSummary, "link">>>({
			queryKey: careDataKeys.observationsOfLinkableForFollowUpPlan(context, params),
			queryFn: () => this.api.getLinkableObservationsForFollowUpPlan(context, params),
			throwOnError: false,
		});
	}

	createObservationLinkMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationLink): Promise<void> =>
				this.api.createLinkBetweenObservations(context, observationLinkToDto(formData)),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservations(context) });
			},
		});
	}

	deleteObservationLinkMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationLink): Promise<void> =>
				this.api.deleteLinkBetweenObservations(context, observationLinkToDto(formData)),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservations(context) });
			},
		});
	}

	getObservationsOfTypeFallQueryOptions(
		context: CareDataContext,
		params: { paging: PagingParams; filters: ObservationOfTypeFallReportFilters },
	) {
		return queryOptions<PagedCollectionBundle<DataBundle<ObservationOfTypeFall>>>({
			queryKey: careDataKeys.observationsOfTypeFall(context, params),
			queryFn: () => this.api.getFallObservationsBySiteId(context, params),
			throwOnError: false,
		});
	}

	downloadObservationsOfTypeFallReportMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: { filters: ObservationOfTypeFallReportFilters }): Promise<FileDownloadBundle> =>
				this.api.downloadSiteFallObservationsXlsx(context, formData),
		});
	}

	getObservationHistoryQueryOptions(context: CareDataContext, params: { observationId: string }) {
		return queryOptions<CollectionBundle<DataBundle<ObservationHistoryEvent>>>({
			queryKey: careDataKeys.observationHistory(context, params),
			queryFn: () => this.api.getObservationHistory(context, params),
			throwOnError: false,
		});
	}

	createFollowUpPlanMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: FollowUpPlanCreation): Promise<{ id: string }> =>
				this.api.createFollowUpPlan(context, convertToFollowUpPlanCreationDto(formData)),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseFollowUpPlanLists(context),
				});
			},
		});
	}

	setFollowUpDateMutationOptions(context: CareDataContext, params: { followUpPlanId: string }) {
		return mutationOptions({
			mutationFn: (formData: { followUpDate: string | null; version: number }): Promise<void> =>
				this.api.setFollowUpDate(context, params, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.base(context),
				});
			},
		});
	}

	uploadFollowUpPlanDocument(context: CareDataContext, file: File) {
		return this.api
			.requestFollowUpPlanUpload(context, { contentType: file.type, contentLength: file.size })
			.pipe(this.s3FileService.uploadWithSignedUrlFor(file));
	}

	submitFollowUpPlanUploadMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: FollowUpPlanUploadSubmission): Promise<void> =>
				this.api.submitFollowUpPlanUpload(context, formData),
			throwOnError: false,
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseFollowUpPlanDetails(context),
				});
			},
		});
	}

	getFollowUpPlansForSiteQueryOptions(
		context: CareDataContext,
		params: { residentId?: string; filters: FollowUpPlanFilters; paging: PagingParams },
	) {
		return queryOptions<PagedCollectionBundle<DataBundleWithActions<FollowUpPlanForSiteSummary, never>>>({
			queryKey: careDataKeys.followUpPlansForSite(context, params),
			queryFn: () => this.api.getFollowUpPlansForSite(context, params).then(convertToFollowUpPlanForSiteSummary),
			throwOnError: false,
		});
	}

	getFollowUpPlanByIdQueryOptions(context: CareDataContext, params: { followUpPlanId: string }) {
		return queryOptions<DataBundleWithActions<FollowUpPlanDetails, "update">>({
			queryKey: careDataKeys.followUpPlanDetails(context, params),
			queryFn: () => this.api.getFollowUpPlanById(context, params).then(convertToFollowUpPlanDetails),
			throwOnError: false,
		});
	}

	saveFollowUpPlanEvaluationMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: FollowUpPlanEvaluationSave): Promise<{ id: string } | void> =>
				"id" in formData
					? this.api.editDraftFollowUpPlanEvaluation(context, formData)
					: this.api.createFollowUpPlanEvaluation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseFollowUpPlans(context),
				});
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseRestrictiveMeasures(context),
				});
			},
		});
	}

	editFinalizedFollowUpPlanEvaluationMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: FinalizedFollowUpPlanEvaluationEdit): Promise<{ id: string } | void> => {
				return this.api.editFinalizedFollowUpPlanEvaluation(context, formData);
			},
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseFollowUpPlanDetails(context),
				});
			},
		});
	}

	getFollowUpPlanEvaluationByIdQueryOptions(
		context: CareDataContext,
		params: { followUpPlanId: string; evaluationId: string },
	) {
		return queryOptions<DataBundleWithActions<EvaluationDetail, "update">>({
			queryKey: careDataKeys.followUpPlanEvaluationDetails(context, params),
			queryFn: () => this.api.getFollowUpPlanEvaluationById(context, { evaluationId: params.evaluationId }),
			throwOnError: false,
		});
	}

	getFollowUpPlanEvaluationsQueryOptions(context: CareDataContext, params: { followUpPlanId: string }) {
		return queryOptions<CollectionBundle<DataBundle<EvaluationSummary>>>({
			queryKey: careDataKeys.followUpPlanEvaluations(context, params),
			queryFn: () => this.api.getEvaluationsForFollowUpPlan(context, params),
			throwOnError: false,
		});
	}

	getFollowUpPlanLinkedObservationsQueryOptions(context: CareDataContext, params: { followUpPlanId: string }) {
		return queryOptions<CollectionBundle<DataBundle<ObservationSummary>>>({
			queryKey: careDataKeys.followUpPlanLinkedObservations(context, params),
			queryFn: () => this.api.getLinkedObservationsForFollowUpPlan(context, params),
			throwOnError: false,
		});
	}

	getFollowUpPlanDocumentsQueryOptions(context: CareDataContext, params: { followUpPlanId: string }) {
		return queryOptions<CollectionBundle<DataBundle<AttachedDocument>>>({
			queryKey: careDataKeys.followUpPlanDocuments(context, params),
			queryFn: () => this.api.getFollowUpPlanDocuments(context, params),
			throwOnError: false,
		});
	}

	getFollowUpPlanDocumentUrlMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: { followUpPlanId: string; documentId: string }): Promise<FileDownloadBundle> =>
				this.api.getFollowUpPlanDocumentUrl(context, formData),
		});
	}

	deleteFollowUpPlanDocumentMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: {
				followUpPlanId: string;
				followUpPlanVersion: number;
				uploadId: string;
				uploadVersion: number;
			}): Promise<void> => this.api.deleteFollowUpPlanDocument(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseFollowUpPlanDetails(context) });
			},
		});
	}

	linkObservationToFollowUpPlanMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: { followUpPlanId: string; observationIds: string[] }): Promise<void> =>
				this.api.linkObservationToFollowUpPlan(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseFollowUpPlans(context) });
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservationLists(context) });
			},
		});
	}

	createPhysicalComplaintObservationMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypePhysicalComplaintCreation): Promise<{ id: string }> =>
				this.api.createPhysicalComplaintObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservationLists(context) });
			},
		});
	}

	getObservationOfTypePhysicalComplaintQueryOptions(context: CareDataContext, params: { observationId: string }) {
		return queryOptions<DataBundleWithActions<ObservationOfTypePhysicalComplaintWithLinks, "update">>({
			queryKey: careDataKeys.observationDetails(context, params),
			queryFn: () => this.api.getPhysicalComplaintObservationById(context, params),
			throwOnError: false,
		});
	}

	editObservationOfTypePhysicalComplaintMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypePhysicalComplaintEdit): Promise<void> =>
				this.api.editPhysicalComplaintObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservations(context) });
			},
		});
	}

	setFollowUpPlanStatusMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: ({
				followUpPlanId,
				status,
				version,
			}: {
				followUpPlanId: string;
				status: FollowUpPlanStatus;
				version: number;
			}): Promise<void> => this.api.setFollowUpPlanStatus(context, { followUpPlanId, status, version }),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseFollowUpPlans(context),
				});
			},
		});
	}

	createNutritionAndHydrationObservationMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeNutritionAndHydrationCreation): Promise<{ id: string }> =>
				this.api.createNutritionAndHydrationObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservationLists(context) });
			},
		});
	}

	getObservationOfTypeNutritionAndHydrationQueryOptions(context: CareDataContext, params: { observationId: string }) {
		return queryOptions<DataBundleWithActions<ObservationOfTypeNutritionAndHydrationWithLinks, "update">>({
			queryKey: careDataKeys.observationDetails(context, params),
			queryFn: () => this.api.getNutritionAndHydrationObservationById(context, params),
			throwOnError: false,
		});
	}

	editObservationOfTypeNutritionAndHydrationMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: ObservationOfTypeNutritionAndHydrationEdit): Promise<void> =>
				this.api.editNutritionAndHydrationObservation(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseObservations(context) });
			},
		});
	}

	editFollowUpPlanMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: FollowUpPlanEdit): Promise<void> =>
				this.api.editFollowUpPlan(context, convertToFollowUpPlanEditDto(formData)),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseFollowUpPlans(context),
				});
			},
		});
	}

	deleteFollowUpPlanObservationLinkMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: FollowUpPlanObservationLink): Promise<void> =>
				this.api.deleteFollowUpPlanObservationLink(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseFollowUpPlans(context) });
			},
		});
	}

	assessmentsOfResidentQueryOptions(context: CareDataContext, params: { residentId: string }) {
		return queryOptions<CollectionBundle<DataBundle<AssessmentSummary>>>({
			queryKey: careDataKeys.assessmentsOfResident(context, params),
			queryFn: () => this.api.getAssessmentSummaries(context, params),
			throwOnError: false,
		});
	}

	assessmentDetailQueryOptions(context: CareDataContext, params: { assessmentType: string; residentId: string }) {
		return queryOptions<AssessmentDetailBundle>({
			queryKey: careDataKeys.assessmentDetail(context, params),
			queryFn: () => this.api.getAssessmentByType(context, params),
			throwOnError: false,
		});
	}

	setAssessmentPlannedDateMutationOptions(
		context: CareDataContext,
		params: { assessmentType: string; residentId: string },
	) {
		return mutationOptions({
			mutationFn: (formData: AssessmentPlannedDateSet): Promise<void> =>
				this.api.setAssessmentPlanningDate(context, { ...params, ...formData }),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseAssessments(context),
				});
			},
		});
	}

	updateAssessmentPlannedDateMutationOptions(
		context: CareDataContext,
		params: { assessmentType: string; residentId: string },
	) {
		return mutationOptions({
			mutationFn: (formData: AssessmentPlannedDateUpdate): Promise<void> =>
				this.api.updateAssessmentPlanningDate(context, { ...params, ...formData }),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseAssessments(context),
				});
			},
		});
	}

	unsetAssessmentPlannedDateMutationOptions(
		context: CareDataContext,
		params: { assessmentType: string; residentId: string },
	) {
		return mutationOptions({
			mutationFn: (formData: AssessmentPlannedDateUnset): Promise<void> =>
				this.api.unsetAssessmentPlanningDate(context, { ...params, ...formData }),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseAssessments(context),
				});
			},
		});
	}

	submitAssessmentMutationOptions(context: CareDataContext, params: { assessmentType: string; residentId: string }) {
		return mutationOptions({
			mutationFn: (formData: AssessmentCreation): Promise<DataBundle<SubmitAssessmentResponse>> =>
				this.api.submitAssessment(context, { ...params, ...formData }),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseAssessments(context),
				});
			},
		});
	}

	uploadSubmittedAssessmentDocument(context: CareDataContext, file: File) {
		return this.api
			.uploadSubmittedAssessmentDocument(context, { contentType: file.type, contentLength: file.size })
			.pipe(this.s3FileService.uploadWithSignedUrlFor(file));
	}

	submitSubmittedAssessmentDocumentUploadsMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: AssessmentUploadSubmission): Promise<void> =>
				this.api.submitSubmittedAssessmentDocumentUploads(context, formData),
			throwOnError: false,
			onSuccess: () => {
				void this.queryClient.invalidateQueries({
					queryKey: careDataKeys.baseAssessmentDetails(context),
				});
			},
		});
	}

	getSubmittedAssessmentDocumentUrlMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (params: { submittedAssessmentId: string; documentId: string }): Promise<FileDownloadBundle> =>
				this.api.getSubmittedAssessmentDocumentUrl(context, params),
		});
	}

	deleteSubmittedAssessmentDocumentMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (formData: DeleteAssessmentUpload): Promise<void> =>
				this.api.deleteSubmittedAssessmentDocument(context, formData),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseAssessmentDetails(context) });
			},
		});
	}

	archiveSubmittedAssessmentMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (params: { submittedAssessmentId: string; version: number }): Promise<void> =>
				this.api.archiveSubmittedAssessment(context, params),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseAssessments(context) });
			},
		});
	}

	enableSubmittedAssessmentMutationOptions(context: CareDataContext) {
		return mutationOptions({
			mutationFn: (params: { submittedAssessmentId: string; version: number }): Promise<void> =>
				this.api.enableSubmittedAssessment(context, params),
			onSuccess: () => {
				void this.queryClient.invalidateQueries({ queryKey: careDataKeys.baseAssessments(context) });
			},
		});
	}

	submittedAssessmentDetailQueryOptions(context: CareDataContext, params: { submittedAssessmentId: string }) {
		return queryOptions<SubmittedAssessmentDetail>({
			queryKey: careDataKeys.submittedAssessmentDetail(context, params),
			queryFn: () => this.api.getSubmittedAssessmentDetail(context, params),
			throwOnError: false,
		});
	}

	submittedAssessmentDocumentsQueryOptions(context: CareDataContext, params: { submittedAssessmentId: string }) {
		return queryOptions<CollectionBundle<DataBundle<AttachedDocument>>>({
			queryKey: careDataKeys.submittedAssessmentDocuments(context, params),
			queryFn: () => this.api.getSubmittedAssessmentDocuments(context, params),
			throwOnError: false,
		});
	}

	restrictiveMeasuresForResidentQueryOptions(context: CareDataContext, params: { residentId: string }) {
		return queryOptions<DataBundle<RestrictiveMeasuresForResident>>({
			queryKey: careDataKeys.restrictiveMeasuresDetails(context, params),
			queryFn: () => this.api.getRestrictiveMeasuresForResident(context, params),
			throwOnError: false,
		});
	}
}
